import { useState, useMemo, useEffect, useCallback, type KeyboardEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Plus, X, LocateFixed, Clock, ArrowUpRight, Loader2 } from 'lucide-react'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { Input } from '@/components/ui/input'
import { SearchSuggestionsDropdown } from '@/components/search/SearchSuggestionsDropdown'
import { getSearchSuggestions, type SearchSuggestion } from '@/lib/searchSuggestions'
import { useSearchIdleSuggestions, useSearchSuggestions } from '@/hooks/api/useSmartSearch'
import { useFilterOptions } from '@/hooks/api/useFilterOptions'
import { useGeolocation } from '@/hooks/useGeolocation'
import { apiSuggestionsToList, idleHistoryToList } from '@/lib/apiSuggestions'
import { topAreasByCount } from '@/lib/areaFilterUtils'
import { useUiStore } from '@/store/uiStore'
import { useCityStore } from '@/store/cityStore'
import { useRecentSearchStore } from '@/store/recentSearchStore'
import { HERO_TABS, heroTabScope, heroTabPremium, heroTabCategory } from '@/lib/site'
import { buildPropertyHref } from '@/lib/propertyNavLinks'
import { markSearchPerformed } from '@/lib/analytics/searchEvents'
import { buildPropertiesSearchUrl, extractIntentFromText } from '@/lib/parseSearchQuery'
import { cn } from '@/lib/utils'

interface DrawerLinkItem {
  id: string
  label: string
  href: string
}

function mergeRecentLinks(
  local: DrawerLinkItem[],
  api: SearchSuggestion[],
  limit = 4,
): DrawerLinkItem[] {
  const seen = new Set<string>()
  const out: DrawerLinkItem[] = []

  for (const item of local) {
    if (seen.has(item.href)) continue
    seen.add(item.href)
    out.push(item)
    if (out.length >= limit) return out
  }

  for (const item of api) {
    if (seen.has(item.href)) continue
    seen.add(item.href)
    out.push({ id: item.id, label: item.title, href: item.href })
    if (out.length >= limit) break
  }

  return out
}

export function GlobalSearchDrawer() {
  const { isSearchOpen, setSearchOpen } = useUiStore()
  const { cityName } = useCityStore()
  const { add: addRecent, items: recentItems } = useRecentSearchStore()
  const [activeTab, setActiveTab] = useState('buy')
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const navigate = useNavigate()

  const tab = HERO_TABS.find((t) => t.id === activeTab) ?? HERO_TABS[0]
  const effectiveIntent = useMemo(() => {
    const q = query.trim()
    if (!q) return tab.intent
    const extracted = extractIntentFromText(q, tab.intent)
    return extracted.explicit ? extracted.intent : tab.intent
  }, [query, tab.intent])
  const tabScope = useMemo(
    () => ({ ...heroTabScope(tab), intent: effectiveIntent }),
    [tab, effectiveIntent],
  )
  const city = cityName.toLowerCase()
  const isTyping = query.trim().length > 0

  const suggestionOpts = useMemo(
    () => ({
      intent: effectiveIntent,
      city: cityName,
      premium: heroTabPremium(tab),
      category: heroTabCategory(tab),
      query,
    }),
    [tab, cityName, query, effectiveIntent],
  )

  const { request: requestLocation } = useGeolocation()
  const { flat: apiFlat, isLoading: suggestionsLoading } = useSearchSuggestions(query, cityName, tabScope)
  const { grouped: idleGrouped, isLoading: idleLoading } = useSearchIdleSuggestions(
    cityName,
    tabScope,
    isSearchOpen && !isTyping,
  )
  const { options: filterOptions } = useFilterOptions(cityName)
  const knownAreas = useMemo(
    () => (filterOptions.areas ?? []).map((a) => a.name).filter(Boolean),
    [filterOptions.areas],
  )

  const suggestions = useMemo(() => {
    const live = apiSuggestionsToList(apiFlat, suggestionOpts)
    if (live.length > 0) return live

    if (!query.trim()) return []

    return getSearchSuggestions({
      query,
      intent: effectiveIntent,
      city: cityName,
      premium: heroTabPremium(tab),
      category: heroTabCategory(tab),
      includeMockInventory: false,
    })
  }, [apiFlat, suggestionOpts, query, tab, cityName, effectiveIntent])

  const apiRecentLinks = useMemo(
    () => idleHistoryToList(idleGrouped.recent, suggestionOpts),
    [idleGrouped.recent, suggestionOpts],
  )

  const lastSearched = useMemo(
    () =>
      mergeRecentLinks(
        recentItems.slice(0, 4).map((item) => ({ id: item.id, label: item.label, href: item.href })),
        apiRecentLinks,
        4,
      ),
    [recentItems, apiRecentLinks],
  )

  const popularLocalities = useMemo(() => {
    const fromFilters = topAreasByCount(filterOptions.areas ?? [], cityName, 10)

    if (fromFilters.length > 0) {
      return fromFilters
    }

    const fromTrending = idleHistoryToList(idleGrouped.trending, suggestionOpts)
      .filter((item) => !/\b(rent|buy|sale|bhk|flat|house|shop|commercial)\b/i.test(item.title))
      .map((item) => ({ id: 0, name: item.title }))

    return fromTrending.slice(0, 10)
  }, [filterOptions.areas, cityName, idleGrouped.trending, suggestionOpts])

  const tryPlaceholder = popularLocalities[0]?.name ?? 'locality'

  useEffect(() => {
    setActiveIndex(0)
  }, [cityName, activeTab])

  useEffect(() => {
    setActiveIndex(0)
  }, [query, activeTab, suggestions.length])

  useEffect(() => {
    if (!isSearchOpen) {
      setQuery('')
    }
  }, [isSearchOpen])

  const close = useCallback(() => {
    setSearchOpen(false)
  }, [setSearchOpen])

  const handleSearch = useCallback(
    (searchQuery = query) => {
      const href = buildPropertiesSearchUrl(searchQuery, {
        intent: tab.intent,
        defaultCity: cityName,
        premium: heroTabPremium(tab),
        tabCategory: heroTabCategory(tab),
        knownAreas,
      })

      addRecent({ label: searchQuery || `${tab.label} in ${cityName}`, href })
      markSearchPerformed('global_search')
      close()
      navigate(href)
    },
    [query, tab, cityName, knownAreas, addRecent, navigate, close],
  )

  const handleSelectSuggestion = useCallback(
    (suggestion: SearchSuggestion) => {
      addRecent({ label: suggestion.title, href: suggestion.href })
      markSearchPerformed('global_search')
      close()
      navigate(suggestion.href)
    },
    [addRecent, navigate, close],
  )

  const localityHref = (area: { id: number; name: string }) => {
    return buildPropertyHref(
      tab.intent === 'rent' ? 'rent' : tab.intent === 'buy' ? 'buy' : tab.intent === 'commercial' ? 'commercial' : '',
      {
        city,
        area: area.id > 0 ? String(area.id) : area.name,
        ...('premium' in tab && tab.premium ? { premium: 'true' } : {}),
        ...('category' in tab && tab.category
          ? { category: tab.category, categorySlug: tab.category === 'New Project' ? 'new-project' : tab.category }
          : {}),
      },
    )
  }

  const detectLocation = () => {
    void requestLocation()
  }

  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (isTyping && suggestions.length > 0) {
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setActiveIndex((index) => Math.min(index + 1, suggestions.length - 1))
        return
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault()
        setActiveIndex((index) => Math.max(index - 1, 0))
        return
      }
    }

    if (event.key === 'Enter') {
      event.preventDefault()
      // Prefer typed query over suggestion click-through so filter phrases
      // like "new project" don't inherit a random project locality.
      handleSearch()
    }
  }

  const mobileTabs = [
    { id: 'buy', label: 'Buy' },
    { id: 'rent', label: 'Rent / PG' },
    { id: 'commercial', label: 'Commercial' },
  ] as const

  return (
    <Sheet open={isSearchOpen} onOpenChange={setSearchOpen}>
      <SheetContent
        side="full"
        showCloseButton={false}
        showDragHandle={false}
        className="flex flex-col bg-[#eef3f9] p-0"
      >
        <SheetTitle className="sr-only">Search properties</SheetTitle>
        <header className="shrink-0 bg-[#eef3f9]">
          <div className="flex items-center gap-2.5 px-4 pb-3 safe-top">
            <div className="flex min-w-0 flex-1 overflow-hidden rounded-lg bg-[#d6e3f0] p-0.5">
              {mobileTabs.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setActiveTab(t.id)}
                  className={cn(
                    'min-w-0 flex-1 truncate rounded-md py-2 text-center text-[11px] font-bold transition-all sm:text-xs',
                    activeTab === t.id
                      ? 'bg-white text-primary-800 shadow-sm'
                      : 'text-primary-800/70',
                  )}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={close}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-neutral-200/80 bg-white text-neutral-500 shadow-sm"
              aria-label="Close search"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="px-4 pb-4">
            <div className="flex h-[50px] items-center gap-2 rounded-xl border border-neutral-200/60 bg-white px-3.5 shadow-[0_2px_12px_rgba(15,93,154,0.08)]">
              <Input
                autoFocus
                placeholder={`Try - ${tryPlaceholder} ${cityName}`}
                className="h-full min-w-0 flex-1 border-0 bg-transparent p-0 text-[14px] shadow-none placeholder:text-neutral-400 focus-visible:ring-0"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onInputKeyDown}
                autoComplete="off"
                role="combobox"
                aria-expanded={isTyping}
              />
              {isTyping ? (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-100"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={detectLocation}
                    className="flex h-7 w-7 shrink-0 items-center justify-center text-primary-600"
                    aria-label="Search near me"
                  >
                    <LocateFixed className="h-[18px] w-[18px]" strokeWidth={2} />
                  </button>
                  <span className="h-5 w-px shrink-0 bg-neutral-200" aria-hidden />
                </>
              )}
            </div>
          </div>
        </header>

        <div className="flex min-h-0 flex-1 flex-col">
          {isTyping && suggestionsLoading && suggestions.length === 0 ? (
            <div className="flex flex-1 items-center justify-center bg-white">
              <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
            </div>
          ) : isTyping && suggestions.length > 0 ? (
            <SearchSuggestionsDropdown
              open
              variant="inline"
              suggestions={suggestions}
              query={query}
              activeIndex={activeIndex}
              onSelect={handleSelectSuggestion}
              onHoverIndex={setActiveIndex}
            />
          ) : isTyping ? (
            <div className="flex flex-1 flex-col items-center justify-center bg-white px-6 text-center">
              <p className="text-[13px] font-medium text-neutral-600">No results found</p>
              <p className="mt-1 text-xs text-neutral-400">Try a different locality or BHK type</p>
            </div>
          ) : (
            <div className="flex-1 space-y-3.5 overflow-y-auto px-4 pb-5 pt-1 hide-scrollbar">
              {idleLoading && lastSearched.length === 0 && popularLocalities.length === 0 ? (
                <div className="flex justify-center py-10">
                  <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
                </div>
              ) : (
                <>
                  {lastSearched.length > 0 && (
                    <section className="rounded-lg border border-neutral-200/60 bg-white p-3.5 shadow-sm">
                      <h3 className="text-sm font-bold text-primary-900">Last searched..</h3>
                      <ul className="mt-1">
                        {lastSearched.map((item) => (
                          <li key={item.id}>
                            <Link
                              to={item.href}
                              onClick={close}
                              className="flex items-center gap-2.5 border-b border-neutral-100 py-2.5 last:border-0"
                            >
                              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-neutral-400">
                                <Clock className="h-3.5 w-3.5" />
                              </span>
                              <span className="min-w-0 flex-1 truncate text-[13px] text-neutral-600">
                                {item.label}
                              </span>
                              <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-neutral-300" />
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}

                  {popularLocalities.length > 0 && (
                    <section className="rounded-lg border border-neutral-200/60 bg-white p-3.5 shadow-sm">
                      <h3 className="text-[13px] font-bold text-primary-900">
                        Popular Localities in {cityName}
                      </h3>
                      <div className="mt-2.5 flex flex-wrap gap-2">
                        {popularLocalities.map((area) => (
                          <Link
                            key={area.id > 0 ? area.id : area.name}
                            to={localityHref(area)}
                            onClick={() => {
                              addRecent({ label: `${area.name}, ${cityName}`, href: localityHref(area) })
                              close()
                            }}
                            className="inline-flex items-center gap-1 rounded-md border border-neutral-200 bg-white px-2 py-1.5 text-[12px] font-medium text-neutral-700 transition-colors hover:border-primary-200 hover:bg-primary-50/40"
                          >
                            <Plus className="h-3 w-3 text-neutral-400" />
                            {area.name}
                          </Link>
                        ))}
                      </div>
                    </section>
                  )}

                  {!idleLoading && lastSearched.length === 0 && popularLocalities.length === 0 && (
                    <div className="rounded-lg border border-neutral-200/60 bg-white p-6 text-center shadow-sm">
                      <p className="text-sm font-medium text-neutral-600">Start typing to search</p>
                      <p className="mt-1 text-xs text-neutral-400">
                        Try a locality, project, or BHK in {cityName}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
